import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { notificationService } from '@/services/notification.service';
import type { Notification } from '@/services/notification.service';

// ─── State ──────────────────────────────────────────────────────────────────

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isDropdownOpen: boolean;
  isLoading: boolean;
  maxNotifications: number;
  currentPage: number;
  hasMore: boolean;

  fetchNotifications: (page?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  loadMore: () => Promise<void>;
  addNotification: (n: Notification) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  toggleDropdown: () => void;
  closeDropdown: () => void;
  reset: () => void;
}

const PAGE_SIZE = 20;

// ─── Selectors ──────────────────────────────────────────────────────────────

export const selectUrgentUnread = (state: NotificationState): Notification[] =>
  state.notifications.filter(
    (n) => !n.is_read && (n.priority === 'URGENT' || n.priority === 'HIGH'),
  );

export const selectRecentNotifications =
  (count: number) =>
  (state: NotificationState): Notification[] =>
    state.notifications.slice(0, count);

// ─── Store ──────────────────────────────────────────────────────────────────

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isDropdownOpen: false,
      isLoading: false,
      maxNotifications: 200,
      currentPage: 1,
      hasMore: false,

      async fetchNotifications(page = 1) {
        set({ isLoading: true });
        try {
          const result = await notificationService.listNotifications({
            page,
            limit: PAGE_SIZE,
          });
          const hasMore = result.meta.page < result.meta.totalPages;
          set({ notifications: result.data, isLoading: false, currentPage: page, hasMore });
        } catch {
          set({ isLoading: false });
        }
      },

      async loadMore() {
        const { currentPage, isLoading, notifications, maxNotifications } = get();
        if (isLoading) return;
        const nextPage = currentPage + 1;
        set({ isLoading: true });
        try {
          const result = await notificationService.listNotifications({
            page: nextPage,
            limit: PAGE_SIZE,
          });
          const hasMore = result.meta.page < result.meta.totalPages;
          const merged = [...notifications, ...result.data].slice(0, maxNotifications);
          set({ notifications: merged, isLoading: false, currentPage: nextPage, hasMore });
        } catch {
          set({ isLoading: false });
        }
      },

      async fetchUnreadCount() {
        try {
          const result = await notificationService.getUnreadCount();
          set({ unreadCount: result.unread_count ?? 0 });
        } catch {
          // Silently fail — unread count is non-critical
        }
      },

      addNotification(n) {
        const { notifications, maxNotifications, unreadCount } = get();
        // Dedupe by id — covers REST refresh + WS push of the same notification.
        if (notifications.some((existing) => existing.id === n.id)) return;
        const updated = [n, ...notifications].slice(0, maxNotifications);
        set({
          notifications: updated,
          unreadCount: n.is_read ? unreadCount : unreadCount + 1,
        });
      },

      async markAsRead(id) {
        // Optimistic update
        const prev = get().notifications;
        const wasUnread = prev.find((n) => n.id === id && !n.is_read);
        set({
          notifications: prev.map((n) =>
            n.id === id ? { ...n, is_read: true } : n,
          ),
          unreadCount: wasUnread ? Math.max(0, get().unreadCount - 1) : get().unreadCount,
        });
        try {
          await notificationService.markAsRead(id);
        } catch {
          // Revert on failure
          set({ notifications: prev, unreadCount: wasUnread ? get().unreadCount + 1 : get().unreadCount });
        }
      },

      async markAllAsRead() {
        const prev = get().notifications;
        const prevCount = get().unreadCount;
        set({
          notifications: prev.map((n) => ({ ...n, is_read: true })),
          unreadCount: 0,
        });
        try {
          await notificationService.markAllAsRead();
        } catch {
          set({ notifications: prev, unreadCount: prevCount });
        }
      },

      toggleDropdown() {
        const isOpen = !get().isDropdownOpen;
        set({ isDropdownOpen: isOpen });
        if (isOpen && get().notifications.length === 0) {
          get().fetchNotifications();
        }
      },

      closeDropdown() {
        set({ isDropdownOpen: false });
      },

      reset() {
        set({
          notifications: [],
          unreadCount: 0,
          isDropdownOpen: false,
          isLoading: false,
          currentPage: 1,
          hasMore: false,
        });
      },
    }),
    { name: 'notification-store' },
  ),
);
